import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live odds ticker overlaid on the full-width Sportsbook hero tile.
 * Fetches a lightweight cached feed; renders nothing if there are no games.
 */
const SportsbookTicker = () => {
    const [feed, setFeed] = useState(null);
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/sports/ticker`)
            .then(({ data }) => { if (alive) setFeed(data); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const count = feed?.games_today > 0 ? feed.games_today : (feed?.upcoming_24h || 0);
    const label = feed?.games_today > 0 ? 'GAMES TODAY' : 'UPCOMING';
    const headlines = feed?.headlines || [];
    if (!count) return null;

    const marquee = [...headlines, ...headlines]; // duplicate for seamless loop
    return (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center gap-3 overflow-hidden bg-gradient-to-t from-black/85 via-black/60 to-transparent px-3 py-2 sm:px-4 sm:py-2.5"
             data-testid="sportsbook-ticker">
            <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#E11D2A] px-2.5 py-1 text-[10px] font-black tracking-wider text-white shadow-[0_0_12px_rgba(225,29,42,0.6)] sm:text-xs"
                  data-testid="sportsbook-ticker-count">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                {count} {label}
            </span>
            <div className="relative flex-1 overflow-hidden">
                <div className="ticker-track flex w-max items-center gap-6 whitespace-nowrap">
                    {marquee.map((m, i) => (
                        <span key={i} className="text-[11px] font-semibold text-white/80 sm:text-xs">
                            <span className="mr-1.5 text-[#FFD700]">●</span>{m}
                        </span>
                    ))}
                </div>
            </div>
            <style>{`
                .ticker-track { animation: btk-scroll 26s linear infinite; }
                @keyframes btk-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
            `}</style>
        </div>
    );
};

/**
 * Official Betohalics dashboard tiles (visual handoff, Jun 2026).
 * Each tile is the supplied 1802×872 artwork used exactly as delivered —
 * the whole tile is the clickable target and opens the existing route.
 * No extra titles / buttons / cards are added; the artwork's own gold
 * frame is the tile border.
 */
const TILES = [
    { key: 'sportsbook',      img: '/tiles/sportsbook.png',       route: '/sportsbook',              flag: 'sportsbook', wide: true },
    // Regular Slots (classic games: Lucky Ducky, Balls of Fire, both Keno,
    // Outlaws Loot, Thunder Cash, Panda) — sits right after Sportsbook, routes to slots lobby.
    { key: 'regular-slots',   img: '/tiles/regular-slots.png',    route: '/casino/slots',            flag: 'casino' },
    { key: 'lottery',         img: '/tiles/lottery.png',          route: '/lottery',                 flag: 'lottery' },
    { key: 'blackjack',       img: '/tiles/blackjack.png',        route: '/casino/blackjack',        flag: 'casino', avail: 'blackjack' },
    { key: 'roulette',        img: '/tiles/roulette.png',         route: '/casino/roulette',         flag: 'casino', avail: 'roulette' },
    { key: 'craps',           img: '/tiles/craps.png',            route: '/casino/craps',            flag: 'casino', avail: 'craps' },
    { key: 'three-card-poker', img: '/tiles/three-card-poker.png', route: '/casino/three-card-poker', flag: 'casino', avail: 'three_card_poker' },
    // Shown when the fish-table service is up (dsf-stage-access); launched to all players 2026-11.
    { key: 'deep-sea-furtune', img: '/tiles/deep-sea-fortune.webp', route: '/casino/deep-sea-furtune', staging: true },
];

const LABELS = {
    sportsbook: 'Sportsbook — Bet Now',
    'regular-slots': 'Betohalics Originals — Play Now',
    'majik-slots': 'Majik Bonus Slots — Play Now',
    lottery: 'Lottery — Play Now',
    blackjack: 'Blackjack — Play Now',
    roulette: 'Roulette — Play Now',
    craps: 'Craps — Play Now',
    'three-card-poker': '3 Card Poker — Play Now',
    'deep-sea-furtune': 'Deep Sea Furtune Fish Table — Play Now',
};

const DashboardTiles = ({ features = {}, gameAvail = {} }) => {
    const navigate = useNavigate();
    const [dsfAllowed, setDsfAllowed] = useState(false);
    useEffect(() => {
        axios.get(`${API}/dsf-stage-access`, { withCredentials: true }).then((r) => setDsfAllowed(!!r.data?.allowed)).catch(() => {});
    }, []);
    const streetDiceEnabled = process.env.REACT_APP_STREET_DICE_ENABLED === 'true';

    const visible = TILES.filter((t) => {
        if (t.staging && !dsfAllowed) return false;
        if (t.flag && features[t.flag] === false) return false;
        // Respect an explicit per-game disable, but show by default.
        if (t.avail && gameAvail[t.avail] === false) return false;
        return true;
    });

    const renderTile = (t, i) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => navigate(t.route)}
                        aria-label={LABELS[t.key]}
                        data-testid={`dashboard-tile-${t.key}`}
                        className={`group relative block w-full overflow-hidden rounded-2xl outline-none transition-transform duration-200 ease-out will-change-transform hover:-translate-y-0.5 hover:shadow-[0_10px_34px_-8px_rgba(255,215,0,0.4)] active:scale-[0.985] focus-visible:ring-2 focus-visible:ring-[#FFD700] focus-visible:ring-offset-2 focus-visible:ring-offset-[#080808] ${t.wide ? 'sm:col-span-2' : ''}`}
                        style={{ aspectRatio: '1802 / 872' }}
                    >
                        <img
                            src={t.img}
                            alt={LABELS[t.key]}
                            width={1802}
                            height={872}
                            loading={i < 2 ? 'eager' : 'lazy'}
                            decoding="async"
                            draggable={false}
                            className="absolute inset-0 h-full w-full object-cover select-none"
                        />
                        {/* restrained hover sheen — pointer devices only, never covers text meaningfully */}
                        <span className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                              style={{ boxShadow: 'inset 0 0 0 1px rgba(255,215,0,0.35)' }} />
                        {t.key === 'sportsbook' && <SportsbookTicker />}
                    </button>
    );

    return (
        <div data-testid="dashboard-tiles">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-5">
                {visible.filter((t) => !t.staging).map(renderTile)}
                {/* Street Dice — official banner artwork, sized to its native ratio. */}
                {streetDiceEnabled && (
                    <button
                        type="button"
                        onClick={() => navigate('/casino/street-dice')}
                        aria-label="Street Dice — Play Now"
                        data-testid="dashboard-tile-street-dice"
                        className="group relative mx-auto block w-full max-w-[90%] overflow-hidden rounded-3xl outline-none transition-transform duration-200 ease-out will-change-transform hover:-translate-y-0.5 hover:shadow-[0_10px_34px_-8px_rgba(255,215,0,0.4)] active:scale-[0.985] focus-visible:ring-2 focus-visible:ring-[#FFD700] focus-visible:ring-offset-2 focus-visible:ring-offset-[#080808]"
                        style={{ aspectRatio: '1802 / 872' }}
                    >
                        <img
                            src="/tiles/street-dice.webp"
                            alt="Street Dice — Play Now"
                            width={2000}
                            height={756}
                            loading="lazy"
                            decoding="async"
                            draggable={false}
                            className="absolute inset-0 h-full w-full object-cover select-none"
                        />
                        <span className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                              style={{ boxShadow: 'inset 0 0 0 1px rgba(255,215,0,0.35)' }} />
                    </button>
                )}
                {/* New games go last on the floor (owner request). */}
                {visible.filter((t) => t.staging).map((t, i) => renderTile(t, i + visible.length))}
            </div>
        </div>
    );
};

export default DashboardTiles;
